import type { TenantBundle, VenueBlock } from './types';

/**
 * Every piece of interaction state in the app. Framework-agnostic on purpose:
 * both apps drive it through `useReducer`, so a behaviour fix lands on web and
 * native at once.
 */

export type SelectedBooth = VenueBlock & { zoneLabel: string };

export type ScreenKey =
  | 'onboard' | 'home' | 'schedule' | 'timeline' | 'talent' | 'talentDetail'
  | 'events' | 'hall' | 'quests' | 'me' | 'cart' | 'search' | 'sessionDetail';

export type TierKey = 'photo' | 'auto' | 'combo' | 'selfie';

export interface Booking {
  step: 0 | 1 | 2 | 3;
  talentId: string;
  tier: TierKey;
  day: string;
  slot: string | null;
}

export interface CartItem {
  talentId: string;
  title: string;
  price: number;
  slot: string;
  dayLabel: string;
  room: string;
}

export interface Voucher {
  title: string;
  meta: string;
  code: string;
}

export interface AppState {
  screen: ScreenKey;
  obStep: number;
  interests: string[];
  obDays: string[];
  day: string;
  schedView: 'time' | 'room';
  track: string;
  talentFilter: string;
  hall: 'map' | 'exhibitors';
  mapLevel: '1' | '2';
  mapZone: string;
  /** The block the attendee tapped on the 3D floor, plus its zone label. */
  booth: SelectedBooth | null;
  exZone: string;
  saved: string[];
  cart: CartItem[];
  vouchers: Voucher[];
  talentId: string | null;
  /** Session open on the detail page. */
  sessionId: string | null;
  booking: Booking | null;
  points: number;
  quizIdx: number;
  quizPick: number | null;
  quizScore: number;
  quizDone: boolean;
  redeemed: string[];
  consents: string[];
  leadSaved: boolean;
  /** Chosen theme variant id, or null for the tenant's base theme. */
  themeId: string | null;
  /** When on, a new variant is picked at every launch instead of persisting one. */
  themeShuffle: boolean;
}

export function initialState(bundle: TenantBundle): AppState {
  return {
    screen: 'onboard',
    obStep: 0,
    interests: [...bundle.onboarding.defaultInterests],
    obDays: [...bundle.onboarding.defaultDays],
    day: bundle.days[Math.min(2, bundle.days.length - 1)]?.key ?? bundle.days[0].key,
    schedView: 'time',
    track: 'All',
    talentFilter: 'All',
    hall: 'map',
    mapLevel: '1',
    mapZone: '',
    booth: null,
    exZone: 'All',
    saved: [],
    cart: [],
    vouchers: [],
    talentId: null,
    sessionId: null,
    booking: null,
    points: bundle.quests.startingPoints,
    quizIdx: 0,
    quizPick: null,
    quizScore: 0,
    quizDone: false,
    redeemed: [],
    consents: [bundle.quests.consents[0]?.id].filter(Boolean) as string[],
    leadSaved: false,
    themeId: bundle.themeVariants?.[0]?.id ?? null,
    themeShuffle: false,
  };
}

export type Action =
  | { type: 'go'; screen: ScreenKey }
  | { type: 'openTalent'; id: string }
  | { type: 'openSession'; id: string }
  | { type: 'setDay'; day: string }
  | { type: 'setSchedView'; view: 'time' | 'room' }
  | { type: 'setTrack'; track: string }
  | { type: 'setTalentFilter'; filter: string }
  | { type: 'setHallTab'; tab: 'map' | 'exhibitors' }
  | { type: 'setMapLevel'; level: '1' | '2' }
  | { type: 'setMapZone'; zone: string }
  | { type: 'setBooth'; booth: AppState['booth'] }
  | { type: 'setExZone'; zone: string }
  | { type: 'toggleSave'; id: string }
  | { type: 'toggleInterest'; label: string }
  | { type: 'toggleObDay'; day: string }
  | { type: 'obNext'; last: boolean }
  | { type: 'obBack' }
  | { type: 'obSkip' }
  | { type: 'restartOnboarding' }
  | { type: 'openBooking'; talentId: string; tier: TierKey; day: string }
  | { type: 'bookingTier'; tier: TierKey }
  | { type: 'bookingDay'; day: string }
  | { type: 'bookingSlot'; slot: string }
  | { type: 'bookingStep'; step: Booking['step'] }
  | { type: 'bookingAddToCart'; item: CartItem }
  | { type: 'closeBooking' }
  | { type: 'removeCartItem'; index: number }
  | { type: 'checkout'; vouchers: Voucher[] }
  | { type: 'quizPick'; index: number; points: number; correct: boolean }
  | { type: 'quizNext'; last: boolean }
  | { type: 'quizReset' }
  | { type: 'redeem'; id: string; cost: number; voucher: Voucher }
  | { type: 'toggleConsent'; id: string }
  | { type: 'saveLead' }
  | { type: 'setTheme'; id: string }
  | { type: 'toggleThemeShuffle' }
  /** Restore-time only: sets both fields without setTheme's side effect. */
  | { type: 'hydrateTheme'; id: string | null; shuffle: boolean }
  | { type: 'locate'; level: '1' | '2' };

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
}

export function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'go':
      return { ...state, screen: action.screen };
    case 'openTalent':
      return { ...state, screen: 'talentDetail', talentId: action.id };
    case 'openSession':
      return { ...state, screen: 'sessionDetail', sessionId: action.id };
    case 'setDay':
      return { ...state, day: action.day };
    case 'setSchedView':
      return { ...state, schedView: action.view };
    case 'setTrack':
      return { ...state, track: action.track };
    case 'setTalentFilter':
      return { ...state, talentFilter: action.filter };
    case 'setHallTab':
      return { ...state, hall: action.tab };
    case 'setMapLevel':
      return { ...state, mapLevel: action.level, booth: null };
    case 'setMapZone':
      return { ...state, mapZone: action.zone };
    case 'setBooth':
      return { ...state, booth: action.booth };
    case 'setExZone':
      return { ...state, exZone: action.zone };
    case 'toggleSave':
      return { ...state, saved: toggle(state.saved, action.id) };
    case 'toggleInterest':
      return { ...state, interests: toggle(state.interests, action.label) };
    case 'toggleObDay':
      return { ...state, obDays: toggle(state.obDays, action.day) };
    case 'obNext':
      return action.last
        ? { ...state, screen: 'home' }
        : { ...state, obStep: state.obStep + 1 };
    case 'obBack':
      return { ...state, obStep: Math.max(0, state.obStep - 1) };
    case 'obSkip':
      return { ...state, screen: 'home' };
    case 'restartOnboarding':
      return { ...state, screen: 'onboard', obStep: 1 };

    case 'openBooking':
      return {
        ...state,
        booking: { step: 0, talentId: action.talentId, tier: action.tier, day: action.day, slot: null },
      };
    case 'bookingTier':
      return state.booking ? { ...state, booking: { ...state.booking, tier: action.tier } } : state;
    case 'bookingDay':
      return state.booking ? { ...state, booking: { ...state.booking, day: action.day, slot: null } } : state;
    case 'bookingSlot':
      return state.booking ? { ...state, booking: { ...state.booking, slot: action.slot } } : state;
    case 'bookingStep':
      return state.booking ? { ...state, booking: { ...state.booking, step: action.step } } : state;
    case 'bookingAddToCart':
      return state.booking
        ? { ...state, cart: [...state.cart, action.item], booking: { ...state.booking, step: 3 } }
        : state;
    case 'closeBooking':
      return { ...state, booking: null };

    case 'removeCartItem':
      return { ...state, cart: state.cart.filter((_, i) => i !== action.index) };
    case 'checkout':
      return { ...state, vouchers: [...state.vouchers, ...action.vouchers], cart: [], screen: 'me' };

    case 'quizPick':
      return {
        ...state,
        quizPick: action.index,
        quizScore: state.quizScore + (action.correct ? 1 : 0),
        points: state.points + (action.correct ? action.points : 0),
      };
    case 'quizNext':
      return action.last
        ? { ...state, quizDone: true, quizPick: null }
        : { ...state, quizIdx: state.quizIdx + 1, quizPick: null };
    case 'quizReset':
      return { ...state, quizIdx: 0, quizPick: null, quizScore: 0, quizDone: false };

    case 'redeem':
      return {
        ...state,
        points: state.points - action.cost,
        redeemed: [...state.redeemed, action.id],
        vouchers: [...state.vouchers, action.voucher],
      };
    case 'toggleConsent':
      return { ...state, consents: toggle(state.consents, action.id), leadSaved: false };
    case 'saveLead':
      return { ...state, leadSaved: true };
    case 'setTheme':
      // Picking a theme by hand is an explicit choice, so it also turns
      // shuffling off — otherwise the next launch would silently discard it.
      return { ...state, themeId: action.id, themeShuffle: false };
    case 'toggleThemeShuffle':
      return { ...state, themeShuffle: !state.themeShuffle };
    case 'hydrateTheme':
      return { ...state, themeId: action.id, themeShuffle: action.shuffle };
    case 'locate':
      return { ...state, screen: 'hall', hall: 'map', mapLevel: action.level, mapZone: '' };

    default:
      return state;
  }
}

/** Only this slice is persisted between launches. Content comes from the CMS. */
export interface PersistedState {
  interests: string[];
  obDays: string[];
  saved: string[];
  vouchers: Voucher[];
  points: number;
  redeemed: string[];
  consents: string[];
  onboarded: boolean;
  themeId: string | null;
  themeShuffle: boolean;
}

export function toPersisted(state: AppState): PersistedState {
  return {
    interests: state.interests,
    obDays: state.obDays,
    saved: state.saved,
    vouchers: state.vouchers,
    points: state.points,
    redeemed: state.redeemed,
    consents: state.consents,
    onboarded: state.screen !== 'onboard',
    themeId: state.themeId,
    themeShuffle: state.themeShuffle,
  };
}

export function applyPersisted(state: AppState, saved: PersistedState | null): AppState {
  if (!saved) return state;
  return {
    ...state,
    interests: saved.interests ?? state.interests,
    obDays: saved.obDays ?? state.obDays,
    saved: saved.saved ?? state.saved,
    vouchers: saved.vouchers ?? state.vouchers,
    points: saved.points ?? state.points,
    redeemed: saved.redeemed ?? state.redeemed,
    consents: saved.consents ?? state.consents,
    themeId: saved.themeId ?? state.themeId,
    themeShuffle: saved.themeShuffle ?? state.themeShuffle,
    screen: saved.onboarded ? 'home' : 'onboard',
  };
}
